import * as React from 'react'
import { Theme } from '@mui/material/styles'
import { ENTITY_TYPES } from '@sales-center/common'
import { BulkReopenFormModel, ValidationCallback } from './typings'
import { RowErrors } from './RowErrors'
import { useFormFieldActions, useValidateRows } from './hooks'

import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { SalesTeamUser } from '@user/components/Leads/ReOpenDialog/ReOpenLeadFormNew'
import { OwnerSelect } from '@user/components/Leads/ReOpenDialog/OwnerSelect'
import { useFormikContext } from 'formik'

const styles = {
  root: {
    fontFamily: 'Source Sans Pro SemiBold',
    marginTop: (theme: Theme) => theme.spacing(0.5),
  },
}

type Props = {
  entityType: ENTITY_TYPES
  validateRows: ValidationCallback
  owners: SalesTeamUser[]
  selectedRowCount: number
}

export const FormFields: React.FC<Props> = (props) => {
  const { entityType, validateRows, owners, selectedRowCount } = props

  const { getFormat } = useLocalizationDateFormat()

  const { expectedClosingDate, onChangeExpectedClosingDate } =
    useFormFieldActions()

  const validationResults = useValidateRows(validateRows)

  const { errors, submitCount } = useFormikContext<BulkReopenFormModel>()

  return (
    <>
      {entityType === ENTITY_TYPES.Opportunity ? (
        <>
          {selectedRowCount > 1 && (
            <>
              <Typography paragraph mt={0.5}>
                Any records assigned to an inactive user will be marked as
                Unassigned when reopening.
              </Typography>

              <Typography paragraph mt={0.5}>
                Any records assigned to a user who is no longer part of the
                record’s sales team will be marked as Unassigned when reopening.
              </Typography>
            </>
          )}

          <Typography sx={{ ...styles.root }}>
            Select a new Expected Closing Date
          </Typography>

          <DatePicker
            label="Expected Closing Date"
            value={expectedClosingDate}
            format={getFormat()}
            onChange={onChangeExpectedClosingDate}
            disablePast={false}
            slotProps={{
              textField: {
                required: true,
                fullWidth: true,
                size: 'small',
                margin: 'normal',
                error: Boolean(errors.expectedClosingDate && submitCount > 0),
              },
            }}
          />
        </>
      ) : (
        <>
          <Typography sx={{ ...styles.root }} gutterBottom>
            Confirm Reopening Leads
          </Typography>
          <Typography paragraph>
            Reopening the selected leads will return them to the first status
            within their assigned sales funnel.
          </Typography>

          {selectedRowCount > 1 && (
            <>
              <Typography paragraph>
                Any records assigned to an inactive user will be marked as
                Unassigned when reopening.
              </Typography>

              <Typography paragraph>
                Any records assigned to a user who is no longer part of the
                record’s sales team will be marked as Unassigned when reopening.
              </Typography>
            </>
          )}
        </>
      )}

      {selectedRowCount === 1 && (
        <>
          <Typography variant="subtitle2" mb={1} mt={1}>
            Select a new Owner
          </Typography>

          <OwnerSelect owners={owners} />
        </>
      )}

      <RowErrors {...validationResults} />
    </>
  )
}
FormFields.displayName = 'FormFields'
